import { useTranslation } from 'react-i18next';
import { uomKeyForOutputItemType } from '@/tools/models/outputItemMetadata.utilities';
import { SeeqNames } from '@seeqdev/constants';

interface OutputItemMetadataLabelProps {
  /** Wire-format item type from the derived feature (e.g. "Signal", "Scalar", "Condition"). */
  outputItemType?: string;
  /**
   * Wire-format metadata map from the source derived/input feature. `undefined` alongside an
   * undefined `outputItemType` means "no restriction at all" (renders "Any"). An input feature can
   * legitimately have `outputItemType` set with `outputItemMetadata` left `undefined` (a type-only
   * restriction, any UOM). That case still renders the type.
   */
  outputItemMetadata?: Record<string, string>;
  /** Omit the bold "Metadata:" prefix, for callers that supply their own label context. */
  hidePrefix?: boolean;
}

const uomLabel = (uom: string | undefined, t: (key: string) => string): string => {
  if (uom === undefined) return t('TEMPLATES.ANY_UOM_LABEL');
  if (uom === '') return t('TEMPLATES.DIMENSIONLESS_UOM_LABEL');
  return uom;
};

// The model PUT wire input accepts SeeqNames.FormulaType.* names ("Signal", "Scalar", "Condition"),
// while the model GET response emits SeeqNames.Types.* names ("Signal", "ScalarItem", "Condition").
// Signal and Condition agree, but Scalar diverges to "ScalarItem" — accept both so labels render on
// both freshly compiled features and round-tripped models. "Measure" is a widening type that accepts
// either Signal or Scalar (backend OutputItemMetadata.Measure).
const outputTypeDisplayName = (outputItemType: string | undefined): string | undefined => {
  switch (outputItemType) {
    case SeeqNames.FormulaType.Condition:
    case SeeqNames.Types.Condition:
      return 'Condition';
    case SeeqNames.FormulaType.Signal:
    case SeeqNames.Types.Signal:
      return 'Signal';
    case SeeqNames.FormulaType.Scalar:
    case SeeqNames.Types.Scalar:
      return 'Scalar';
    case 'Measure':
      return 'Measure';
    default:
      return undefined;
  }
};

const buildTypeAndUomLabel = (
  outputItemType: string | undefined,
  outputItemMetadata: Record<string, string>,
  t: (key: string) => string,
): string | undefined => {
  const displayName = outputTypeDisplayName(outputItemType);
  if (displayName === undefined) {
    return undefined;
  }
  // The UOM key (ValueUom for Signal/Measure, Uom for Scalar, none for Condition) is the single
  // rule shared with the metadata projection.
  const uomKey = uomKeyForOutputItemType(outputItemType);
  if (uomKey === undefined) {
    return displayName;
  }
  return `${displayName} (${uomLabel(outputItemMetadata[uomKey], t)})`;
};

/**
 * Inline annotation describing a model derived/input feature's output item restriction. Rendered
 * with a bold "Metadata:" prefix that matches the "Name:" prefix used by `FormulaItemTable` rows.
 *
 *  - both fields `undefined` → renders "Any" (no restriction).
 *  - `outputItemType` set (with `outputItemMetadata` a map, or left `undefined` for a type-only,
 *    any-UOM restriction) → renders the type plus the UOM annotation when applicable.
 *
 * Renders nothing only when a type is present but unrecognised (defensive fallback).
 */
export const OutputItemMetadataLabel = ({
  outputItemType,
  outputItemMetadata,
  hidePrefix = false,
}: OutputItemMetadataLabelProps) => {
  const { t } = useTranslation();

  // Input features may have a type without metadata, so only treat them as unrestricted when both are unset.
  const value =
    outputItemType === undefined && outputItemMetadata === undefined
      ? t('TEMPLATES.ANY_TYPE_LABEL')
      : buildTypeAndUomLabel(outputItemType, outputItemMetadata ?? {}, t);

  if (value === undefined) {
    return null;
  }

  return (
    <span data-testid="outputItemMetadataLabel">
      {!hidePrefix && <span className="tw:font-bold">{t('TEMPLATES.METADATA_LABEL')}:</span>} {value}
    </span>
  );
};
